import { useSteps, StepLayoutChildProps, Step } from '@consigli/facade';
import {
  BuildingPackage,
  BuildingPackageType,
  BuildingPackageWorkflowStep,
  PipelineName,
} from '@consigli/types';
import { FC, useState, useCallback } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { LoadCalculationWizardLayout } from './load-calculation-layout';
import { LoadCalculationUploadFilesStep } from './load-calculation-upload-files-step';
import { useCreatePackageUtil } from '../hooks/use-create-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export type BuildingPackageResponse = Promise<BuildingPackage | undefined>;

export const LoadCalculationWizard = () => {
  const { t } = useTranslation();

  const methods = useForm<FieldValues>({
    defaultValues: {
      iterationName: '',
      language: '',
    },
  });
  const { getValues, handleSubmit } = methods;

  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);
  const { onAnalyze: onPackageCreation, startAnalysis } = useCreatePackageUtil({
    setCurrentCounter,
    setTargetCounter,
  });

  const onLoadCalculationPackageCreation = useCallback(async (): BuildingPackageResponse => {
    try {
      const response = await onPackageCreation(
        localFiles,
        getValues(),
        BuildingPackageType.LOAD_CALCULATION,
        BuildingPackageWorkflowStep.PROCESSING_LOAD_CALCULATION_STEP,
      );
      if (response && response.type === BuildingPackageType.LOAD_CALCULATION) {
        await startAnalysis(response.id, PipelineName.LOAD_CALCULATION);
        return response;
      }
    } catch (error) {
      console.error(t(`package-creation.failed-analyzed`, { error }));
      toast.error(t(`package-creation.failed-analyzed`));
      return undefined;
    }
  }, [onPackageCreation, startAnalysis, localFiles, getValues, t]);

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const { withStep, StepIndicator, StepOutlet } = useSteps(
    (step: (name: string, component: FC<StepLayoutChildProps>) => Step) => [
      step(t('createproject.uploadfiles'), () => (
        <FormProvider {...methods}>
          <form className="w-full" onSubmit={handleSubmit(onLoadCalculationPackageCreation)}>
            <LoadCalculationUploadFilesStep
              localFiles={localFiles}
              setLocalFiles={setLocalFiles}
              onLoadCalculationPackageCreation={onLoadCalculationPackageCreation}
            />
          </form>
        </FormProvider>
      )),
    ],
  );

  const stepsRenderer = withStep(() => (
    <>
      <StepIndicator />
      <StepOutlet />
    </>
  ));

  return (
    <LoadCalculationWizardLayout
      stepsRenderer={stepsRenderer}
      isUploading={isUploading}
      progress={progress}
    />
  );
};
